import { useCallback, useEffect, useState, useContext } from "react";
import { Outlet, useLocation } from "react-router-dom";
import { useMsal, useIsAuthenticated } from "@azure/msal-react";
import { InteractionStatus } from "@azure/msal-browser";
import debounce from "lodash/debounce";
import { useDispatch, useSelector } from "react-redux";

import "./Layout.scss";

import Header from "../header/Header";
import { Footer } from "../footer/Footer";
import SsoLogin from "../../components/SignInModule/SSO-SignIn/SignIn";
import Loader from "../Loader/Loader";
import RegistartionPage from "../../components/SignInModule/Registration/Registration";
import ForgotPasswordPage from "../../components/SignInModule/Forgot Password/ForgotPasword";
import { getUserDetails } from "../../services/AdminService";
import CdpAuthPage from "../../pages/Cdp/CdpAuth";
import ConfirmEmail from "../../components/NotificationSettings/ConfirmEmail/ConfirmEmail";
import { setAccessToken, setUserInfo, setUserLoggedIn } from "../../store/userInfo/userInfo.slice";
import axiosHttp from "../../services/interceptor";
import { ClaimsContext } from "../../services/ClaimsService";

const Layout = () => {
	const dispatch = useDispatch();
	const location = useLocation();
	const { setIsAuthenticated } = useContext(ClaimsContext);
	const showFreedomHeaderFooter = process.env.REACT_APP_SHOW_FREEDOM_HEADERFOOTER === "true";

	const userSignIn = useSelector((state) => state.userInfo.isUserLoggedIn);

	const { accounts, instance, inProgress } = useMsal();
	const isMsAuthenticated = useIsAuthenticated();
	const [authenticate, setAuthenticate] = useState(false);
	const [confirmToken, setConfirmToken] = useState(false);
	const [headerFooterEnabled, setHeaderFooterEnabled] = useState(() => {
		const storedValue = sessionStorage.getItem("header-footer");
		return storedValue !== null ? JSON.parse(storedValue) : true;
	});

	// Create a debounced setter function
	const debouncedSetHeaderFooterEnabled = debounce((value) => {
		setHeaderFooterEnabled(value);
	}, 100);
	const [authToken, setAuthToken] = useState(null);
	const [headerHeight, setHeaderHeight] = useState("auto");
	const [footerHeight, setFooterHeight] = useState("380px");
	const [headerProps, setHeaderProps] = useState({
		width: "100%",
		title: "Header",
		scrolling: "auto",
		frameBorder: "0",
	});
	const [footerProps, setFooterProps] = useState({
		width: "100%",
		title: "Footer",
		scrolling: "auto",
		frameBorder: "0",
	});

	const isADLoggedIn = authToken !== null;
	const isUserLoggedIn = userSignIn?.toString() === "true";
	const isAuthenticated = isADLoggedIn || isUserLoggedIn;
	useEffect(() => {
		setIsAuthenticated(isAuthenticated);
	}, [isAuthenticated, setIsAuthenticated]);

	//on refresh, get user data on api call
	useEffect(() => {
		const fetchUserDetails = async () => {
			try {
				const response = await getUserDetails();
				if (response) {
					dispatch(setUserLoggedIn(true));
					dispatch(setUserInfo(response));
				} else {
					dispatch(setUserLoggedIn(false));
				}
			} catch (error) {
				console.error("Error fetching user details:", error);
			}
		};

		fetchUserDetails();
	}, [authToken]);

	useEffect(() => {
		sessionStorage.setItem("header-footer", true);
		if (location.pathname === "/confirmEmail") {
			sessionStorage.setItem("header-footer", false);
			setConfirmToken(true);
		}

		if (sessionStorage.hasOwnProperty("wcn")) {
			sessionStorage.setItem("header-footer", false);
		}
	}, [location.pathname]);

	const messageListener = (e) => {
		if (e.data?.event) {
			const isResize = e.data?.event === "resizeheader";
			const isProps = e.data?.event === "headerProps" && e.data?.props && typeof e.data.props === "object";
			isResize && setHeaderHeight(e.data.height);
			isProps &&
				setHeaderProps((currentProps) => ({
					...currentProps,
					...e.data.props,
				}));
		}
	};

	const clickListener = () => setHeaderHeight(70);

	const resizeListener = () => dispatchMessage({ windowResize: { width: window.innerWidth } });

	const listeners = [
		["message", messageListener],
		["resize", resizeListener],
		["click", clickListener],
	];

	const handleContainerClick = () => {
		const payload = {
			documentClick: "true",
			event: "documentClick",
		};
		dispatchMessage(payload);
	};

	const aemLoc = (target) => `https://clouddev.eagle.org/contentservice/${target}?appID=myFreedom`;

	const loginRequest = {
		scopes: [],
	};

	const dispatchMessage = (payload) => {
		const headerContent = document.getElementById("ifrmHeader");
		headerContent?.contentWindow?.postMessage(payload, "*");
	};

	const getToken = async () => {
		try {
			const account = accounts[0];
			const tokenResult = await instance.acquireTokenSilent({
				...loginRequest,
				account: account,
			});
			if (tokenResult) {
				const token =
					tokenResult?.accessToken ||
					tokenResult?.account?.idToken ||
					tokenResult?.idToken;

				if (token) {
					await handleAccessToken(token);
					setAuthToken(token);
					dispatch(setAccessToken(token));
				}
			}
		} catch (error) {
			console.error("Failed to acquire token:", error);
		}
	};
	const handleAccessToken = async (token) => {
		try {
			const res = await axiosHttp({
				url: `api/Accounts/StoreCookie`,
				method: "POST",
				data: { accessToken: token },
			});
		} catch (error) {
			console.error(error);
		}
	};
	// Use the debounced function
	useEffect(() => {
		const storedValue = sessionStorage.getItem("header-footer");
		if (storedValue !== null) {
			debouncedSetHeaderFooterEnabled(JSON.parse(storedValue));
		}
	}, [debouncedSetHeaderFooterEnabled]);

	useEffect(() => {
		if (inProgress !== InteractionStatus.None) return;

		if (!isMsAuthenticated && accounts.length === 0) {
			// Handle not logged in state
		} else if (accounts.length > 0) {
			setAuthenticate(true);
			getToken();

			if (showFreedomHeaderFooter && headerFooterEnabled) {
				const INIT_OBJECT = {
					aemUrl: aemLoc("header"),
					user: accounts[0].name,
					releaseVersion: "1",
					buildTime: "1",
				};
				window.initHeader?.(INIT_OBJECT);
				window.initFooter?.({
					...INIT_OBJECT,
					aemUrl: aemLoc("footer"),
				});
			}
		}
	}, [inProgress]);

	useEffect(() => {
		const pageWrapper = document.getElementById("page-wrapper");
		pageWrapper?.addEventListener("click", handleContainerClick);
		listeners.forEach((l) => window.addEventListener(l[0], l[1]));
		return () => {
			listeners.forEach((l) => window.removeEventListener(l[0], l[1]));
			pageWrapper?.removeEventListener("click", handleContainerClick);
		};
	}, []);

	const triggerOnLoad = () => {
		const user = "user";
		const host = window.location;
		const environment = "https://cdp-dev.eagle.org/";
		const copyright = `ABS &copy; ${new Date().getFullYear()} All Rights Reserved`;
		const buildTime = "buildTime";
		const payLoad = {
			headerLoaded: true,
			copyright,
			buildTime,
			user,
			host,
			environment,
		};
		dispatchMessage(JSON.parse(JSON.stringify(payLoad)));
	};

	if (inProgress === "startup" || inProgress === "handleRedirect" || userSignIn === "not") {
		return <Loader loaderName="loading..." />;
	}

	return (
		<>
			{confirmToken ? (
				<ConfirmEmail />
			) : authToken || userSignIn ? (
				<>
					{showFreedomHeaderFooter && headerFooterEnabled && (
						<>
							{/* <div id="HeaderWrapper" style={{ height: "70px" }}>
								<iframe
									src={aemLoc("header")}
									id="ifrmHeader"
									title="ABSHeader"
									height={headerHeight}
									onLoad={triggerOnLoad}
									{...headerProps}
									style={{
										position: "fixed",
										width: "100%",
										top: 0,
										left: 0,
										zIndex: 9999,
									}}
								/>
							</div> */}
							<Header />
						</>
					)}
					<div className="layout-body">
						<Outlet />
					</div>
					{/* {showFreedomHeaderFooter && headerFooterEnabled && (
						<>
							<iframe
								src={aemLoc("footer")}
								id="ifrmFooter"
								height={footerHeight}
								onLoad={triggerOnLoad}
								width="100%"
								title="footer"
								scrolling="no"
								frameBorder="0"
								{...footerProps}
							/>
							<Footer />
						</>
					)} */}
				</>
			) : (
				<div>
					{inProgress === "logout" ? (
						<span className="d-flex justify-content-center align-items-center">
							<Loader loaderName="Signing Out" />
						</span>
					) : (
						<>
							{location.pathname !== "/register" && location.pathname !== "/forgotpassword" && <SsoLogin />}
							{location.pathname === "/register" && <RegistartionPage />}
							{location.pathname === "/forgotpassword" && <ForgotPasswordPage />}
							{location.pathname === "/cdpauth" && <CdpAuthPage />}
							{location.pathname === "/confirmEmail" && <ConfirmEmail />}
						</>
					)}
				</div>
			)}
		</>
	);
};

export default Layout;
